import { type QueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { type GetBillingInfoResponse } from '../../network/httpClients/BillingClient/api.schema';
import { OIDCProvider } from '../../network/httpClients/OIDCAuthenticationClient/api.schema';
import {
  type Organization,
  type SnakedOrganization,
  type User,
} from '../../network/httpClients/PlatformClient/api.schema';
import { QUERY_KEYS } from '../../network/queries/consts';
import {
  type AccountInfoResponse,
  type QuotaResponse,
} from '../../types/billing';
import { isOIDCError } from '../../utils/authentication';
import { transformKeysToCamelCase } from '../../utils/caseTransformers';
import { EXPECTED_ERROR_INTERNAL_STATUS_CODES } from './consts';
import {
  type BaseProperties,
  EventProperties,
  type MixpanelSubscriptionInfo,
  type UserData,
} from './types';

/**
 * Builds a standardized error data object for Mixpanel tracking
 * @param error - The error object (can be OIDC error or AxiosError)
 * @param errorLocation - The location where the error occurred
 * @returns ErrorData object with standardized error information
 */
export const buildMixpanelErrorData = (
  error: unknown,
  errorLocation: OIDCProvider,
): EventProperties['error_data'] => {
  const errorCode = isOIDCError(error)
    ? String(error.error.details?.code)
    : (error as AxiosError)?.code;

  const errorMessage = isOIDCError(error)
    ? error.error.message
    : (error as AxiosError)?.message;

  const isExpectedError = isOIDCError(error)
    ? EXPECTED_ERROR_INTERNAL_STATUS_CODES.includes(
        error.error.internal_status_code,
      )
    : false;

  return {
    error_code: errorCode || 'unknown',
    error_message: errorMessage || 'unknown',
    error_location: errorLocation,
    is_expected_error: isExpectedError,
  };
};

/**
 * The `user_data` join keys resolved once per event.
 */
export type MixpanelCommonEventFields = Pick<
  UserData,
  'account_id' | 'workspace_id' | 'workspace_created_at'
>;

/**
 * Reads the cached organization and camel-cases it.
 *
 * `getQueryData` returns the cache entry as stored, before `select` runs, so
 * the value is the raw snake_case wire payload: reading `accountId` straight
 * off it always yields undefined. Mirrors the unwrap in `useOrganizationQuery`,
 * which accepts both the `{ organization }` envelope and a bare organization.
 *
 * @param queryClient - the app QueryClient (undefined before Mixpanel init)
 * @returns the camel-cased organization, or undefined when nothing is cached
 */
export const getCachedOrganization = (
  queryClient: QueryClient | undefined,
): Organization | undefined => {
  const cached = queryClient?.getQueryData([QUERY_KEYS.ORGANIZATION]) as
    | { organization?: SnakedOrganization }
    | undefined;

  const organization =
    cached?.organization ?? (cached as SnakedOrganization | undefined);

  return organization
    ? (transformKeysToCamelCase(organization) as Organization)
    : undefined;
};

/**
 * Resolves the activation-funnel join keys carried in `user_data` on every
 * Mixpanel event.
 *
 * Prefers the user object because `/users` is cached app-wide (the sidebar
 * mounts it on every authenticated page), while `/organizations` is only
 * fetched on a handful of surfaces: sourcing from the organization alone left
 * these keys missing on most events. The organization stays as a fallback for
 * sessions where the user record predates the account/workspace backfill.
 *
 * @param user - the cached, camel-cased user, if loaded
 * @param organization - the cached organization, if loaded
 * @returns only the fields that resolved to a value
 */
export const buildCommonEventFields = (
  user: User | undefined,
  organization: Organization | undefined,
): MixpanelCommonEventFields => {
  const accountId = user?.accountId || organization?.accountId;
  const workspaceId = user?.workspaceId || organization?.workspaceId;
  const workspaceCreatedAt = user?.workspaceCreatedAt;

  return {
    ...(accountId && { account_id: accountId }),
    ...(workspaceId && { workspace_id: workspaceId }),
    ...(workspaceCreatedAt && { workspace_created_at: workspaceCreatedAt }),
  };
};

/**
 * Builds the billing + account context attached to every Mixpanel event
 * (PLT-4031). Reads the globally-cached /account-info and /quota queries from
 * the React Query cache so each event can be segmented by tier / trial / plan /
 * usage without any call site passing it (mirrors how `user_data` is attached).
 *
 * Both sources are meta-level: /account-info is fetched at the app root and
 * /quota by the always-mounted header pill + sidebar widget, so they're cached
 * app-wide. (/billing-info is NOT global — see {@link buildSubscriptionContext}.)
 *
 * Best-effort and side-effect-free: returns only the blocks whose query is
 * cached, never triggers a fetch. Both queries use a dynamic `[key, scope,
 * source]` key, so they are matched by prefix.
 *
 * @param queryClient - the app QueryClient (undefined before Mixpanel init)
 * @returns the `account_info` / `billing_info` base-property blocks that are available
 */
export const buildBillingEventContext = (
  queryClient: QueryClient | undefined,
): Pick<BaseProperties, 'account_info' | 'billing_info'> => {
  if (!queryClient) {
    return {};
  }

  const context: Pick<BaseProperties, 'account_info' | 'billing_info'> = {};

  const accountInfo = queryClient
    .getQueriesData<AccountInfoResponse>({
      queryKey: [QUERY_KEYS.BILLING_ACCOUNT_INFO],
    })
    .find(([, data]) => Boolean(data))?.[1];

  if (accountInfo) {
    const trial = accountInfo.trial;

    context.account_info = {
      self_serve: accountInfo.selfServe,
      tier: accountInfo.tier,
      ...(trial && {
        trial: {
          active: trial.active,
          days_remaining: trial.daysRemaining,
          day_number: trial.dayNumber,
          total_days: trial.totalDays,
        },
      }),
    };
  }

  const quota = queryClient
    .getQueriesData<QuotaResponse>({
      queryKey: [QUERY_KEYS.BILLING_QUOTA_BALANCE],
    })
    .find(([, data]) => Boolean(data))?.[1];

  if (quota) {
    context.billing_info = {
      plan_name: quota.plan?.name,
      credits_granted: quota.plan?.credits_granted,
      credits_remaining: quota.plan?.credits_remaining,
      usage_pct: quota.usage_pct,
      overage_active: quota.overage?.active,
      blocked_reason: quota.blocked?.reason,
    };
  }

  return context;
};

/**
 * Maps the /billing-info subscription response to the `subscription_info`
 * event block (PLT-4031). Used at the conversion-funnel track sites only —
 * /billing-info is fetched on those surfaces, not app-wide, so this context is
 * scoped to the funnel events rather than attached globally. No PII.
 *
 * @param billingInfo - the /billing-info query data, if loaded
 * @returns the `subscription_info` block, or undefined when no data is available
 */
export const buildSubscriptionContext = (
  billingInfo: GetBillingInfoResponse | undefined,
): MixpanelSubscriptionInfo | undefined => {
  if (!billingInfo) {
    return undefined;
  }

  return {
    subscription_state: billingInfo.subscriptionState,
    plan_sku: billingInfo.planSku,
    plan_name: billingInfo.planName,
    monthly_usd: billingInfo.monthlyUsd,
    cancel_at: billingInfo.cancelAt,
  };
};
